import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ROUTES } from '../../common-files/enums/server-routes';

// Operator (central provisioning) API calls. CENTRAL-scoped, so the
// x-establishment-id header sent by the interceptor is ignored by the backend.
@Injectable({ providedIn: 'root' })
export class ProvisioningService {
  constructor(private http: HttpClient) {}

  getDashboardCounts(): Observable<any> {
    return this.http.get(ROUTES.PROV_DASHBOARD_COUNTS);
  }

  // locate a requisition by id or Aadhaar (same requisitionListColumns shape).
  searchById(bookingId: number): Observable<any> {
    return this.http.get(`${ROUTES.PROV_REQUISITION}/${bookingId}`);
  }

  searchByAadhaar(aadhaar: string): Observable<any> {
    const params = new HttpParams().set('aadhaar', aadhaar);
    return this.http.get(`${ROUTES.PROV_REQUISITION}/by-aadhaar`, { params });
  }

  // -- queue --
  searchByQueue(payload: any): Observable<any> {
    return this.http.post(ROUTES.PROV_QUEUE, payload);
  }

  getQueueCounts(): Observable<any> {
    return this.http.get(ROUTES.PROV_QUEUE_COUNTS);
  }

  // roll back every aged operator lock in the working window
  rollbackBulkAll(): Observable<any> {
    return this.http.post(ROUTES.PROV_REQ_BULK_ROLLBACK_ALL, {});
  }

  // -- daily reports (dashboard exports) --
  getDailyConfirmed(date: string): Observable<any> {
    return this.http.get(ROUTES.PROV_REPORT_CONFIRMED, { params: { date } });
  }

  getDailyAllocations(date: string): Observable<any> {
    return this.http.get(ROUTES.PROV_REPORT_ALLOCATIONS, { params: { date } });
  }

  lockOldest(payload: any): Observable<any> {
    return this.http.post(ROUTES.PROV_LOCK_OLDEST, payload);
  }

  getCancelReasons(): Observable<any> {
    return this.http.get(ROUTES.PROV_CANCEL_REASONS);
  }

  // -- per-requisition actions (Operator does lock + provision end to end) --
  provision(bookingId: number, payload: any): Observable<any> {
    return this.http.post(`${ROUTES.PROV_REQUISITION}/${bookingId}/provision`, payload);
  }

  rollback(bookingId: number, timed: boolean): Observable<any> {
    return this.http.post(`${ROUTES.PROV_REQUISITION}/${bookingId}/rollback`, { timed });
  }

  cancel(bookingId: number, payload: any): Observable<any> {
    return this.http.post(`${ROUTES.PROV_REQUISITION}/${bookingId}/cancel`, payload);
  }

  forwardToAdmin(bookingId: number, payload: any): Observable<any> {
    return this.http.post(`${ROUTES.PROV_REQUISITION}/${bookingId}/forward-admin`, payload);
  }

  // send a CONFIRMED booking to the admin queue for cancellation
  forwardCancel(bookingId: number, payload: { forwardRemark?: string }): Observable<any> {
    return this.http.post(`${ROUTES.PROV_REQUISITION}/${bookingId}/forward-cancel`, payload);
  }

  // -- manual provisioning (walk-in) --
  manualPremises(payload: any): Observable<any> {
    return this.http.post(ROUTES.PROV_MANUAL_PREMISES, payload);
  }

  manualAccommodationSets(payload: any): Observable<any> {
    return this.http.post(ROUTES.PROV_MANUAL_SETS, payload);
  }

  manualRooms(payload: any): Observable<any> {
    return this.http.post(ROUTES.PROV_MANUAL_ROOMS, payload);
  }

  manualProvision(payload: any): Observable<any> {
    return this.http.post(ROUTES.PROV_MANUAL_PROVISION, payload);
  }

  // -- devotee utilities --
  getFcHistory(familyCode: string): Observable<any> {
    return this.http.get(`${ROUTES.PROV_DEVOTEE}/${familyCode}/requisitions`);
  }

  searchByDistrict(district: string, fromDate: string, toDate: string): Observable<any> {
    const params = new HttpParams()
      .set('district', district)
      .set('fromDate', fromDate)
      .set('toDate', toDate);
    return this.http.get(ROUTES.PROV_BY_DISTRICT, { params });
  }

  confirmSms(bookingId: number): Observable<any> {
    return this.http.post(`${ROUTES.PROV_REQUISITION}/${bookingId}/confirm-sms`, {});
  }
}
